import React from "react";
import { Button, Chip } from "@mui/material";

// ============================================================
// Date helpers
// The API sends two date formats:
//   ISO:        2026-10-19T22:57:08
//   DD-MM-YYYY: 10-11-2026 12:10:47   (active_subscription_end, subscribed_on)
// Both are parsed into real Date objects so sorting and filtering work.
// ============================================================
export const parseDate = (value) => {
    if (!value || typeof value !== "string") return null;

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }

    const m = value.match(
        /^(\d{2})-(\d{2})-(\d{4})(?: (\d{2}):(\d{2}):(\d{2}))?$/,
    );
    if (m) {
        const [, dd, mm, yyyy, hh = "0", mi = "0", ss = "0"] = m;
        return new Date(+yyyy, +mm - 1, +dd, +hh, +mi, +ss);
    }
    return null;
};

const pad = (n) => String(n).padStart(2, "0");

export const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    return `${pad(date.getDate())}-${pad(
        date.getMonth() + 1,
    )}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Date filter hands us midnight of the picked day; compare by calendar day only
const dateOnlyComparator = (filterDate, cellValue) => {
    if (!cellValue) return -1;
    const cell = new Date(
        cellValue.getFullYear(),
        cellValue.getMonth(),
        cellValue.getDate(),
    );
    if (cell < filterDate) return -1;
    if (cell > filterDate) return 1;
    return 0;
};

const cellText = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
};

const flexEnd = {
    display: "flex",
    width: "100%",
    minWidth: 0,
    height: "100%",
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "flex-end",
    overflow: "hidden",
};

const CellValue = ({ children, align = "end" }) => (
    <div
        style={{
            ...flexEnd,
            justifyContent: align === "start" ? "flex-start" : "flex-end",
        }}
    >
        <span style={cellText}>{children}</span>
    </div>
);

// ============================================================
// Renderers (existing ones kept, made null-safe)
// ============================================================
export const DefaultRenderer = (props) => {
    return (
        <CellValue>
            {props.value != undefined && props.value !== "" ? props.value : "-"}
        </CellValue>
    );
};

export const DefaultRendererWithLocale = (props) => {
    return (
        <CellValue>
            {props.value != undefined
                ? props.value.toLocaleString(props.locale_string)
                : "-"}
        </CellValue>
    );
};

export const DateRenderer = (props) => {
    return <CellValue>{props.value?.date ?? "-"}</CellValue>;
};

// Shows the formatted text of a column that uses valueGetter + valueFormatter
export const DateTimeRenderer = (props) => {
    return <CellValue>{props.valueFormatted || "-"}</CellValue>;
};

export const DefaultBooleanRenderer = (props) => {
    return (
        <CellValue>
            {props.value != undefined && props.value !== ""
                ? props.value
                    ? "True"
                    : "False"
                : "-"}
        </CellValue>
    );
};

export const SubscriberRenderer = (props) => {
    return (
        <CellValue>
            {props.value != undefined && props.value !== ""
                ? props.value
                    ? "PAID"
                    : "-"
                : "-"}
        </CellValue>
    );
};

export const RolesRenderer = (props) => {
    const text =
        Array.isArray(props.value) && props.value.length
            ? props.value.join(", ")
            : "-";
    return <CellValue>{text}</CellValue>;
};

export const ConsumedOrderRenderer = (props) => {
    const list = Array.isArray(props.value) ? props.value : [];
    return (
        <CellValue align="start">
            {list.length ? list[list.length - 1] : "-"}
        </CellValue>
    );
};

export const SubscriptionListRenderer = (props) => {
    const active = Array.isArray(props.value)
        ? props.value.filter((order) => order.subscription_status == "ACTIVE")
        : [];
    const text =
        active.length > 0
            ? active
                  .map(
                      (order) =>
                          `${order.subscription_name}: ${order.order_status}/${order.subscription_status} (${order.subscription_age})`,
                  )
                  .join("; ")
            : "-";
    return <CellValue align="start">{text}</CellValue>;
};

export const TimeRenderer = (props) => {
    const parts =
        typeof props.value === "string" ? props.value.split(":") : null;
    const text =
        parts && parts.length >= 2
            ? `${parseInt(parts[0], 10)} Hours, ${parts[1]} Mins`
            : "-";
    return <CellValue>{text}</CellValue>;
};

export const DaysLeftRenderer = (props) => {
    const v = props.value;
    if (v === undefined || v === null || v === "")
        return <CellValue>-</CellValue>;
    const color = v <= 3 ? "#ef5350" : v <= 7 ? "#ffa726" : "inherit";
    return (
        <div
            style={{
                ...flexEnd,
                justifyContent: "flex-end",
            }}
        >
            <span
                style={{
                    ...cellText,
                    color,
                    fontWeight: color === "inherit" ? 400 : 600,
                }}
            >
                {v}
            </span>
        </div>
    );
};

const STATUS_COLORS = {
    SUCCESS: "success",
    ACTIVE: "success",
    INITIATED: "warning",
    PENDING: "warning",
    REJECTED: "error",
    CANCELLED: "error",
    CONSUMED: "default",
};

// Used in the order history dialog
export const StatusRenderer = (props) => {
    if (!props.value) return <>-</>;
    const color = STATUS_COLORS[props.value] ?? "default";
    return (
        <Chip
            label={props.value}
            size="small"
            color={color}
            variant={color === "default" ? "outlined" : "filled"}
        />
    );
};

// Button that opens the order history dialog.
// `context.openOrders` comes from <AgGridReact context={{ openOrders: ... }} />
export const OrderRecordsRenderer = (props) => {
    const n = Array.isArray(props.value) ? props.value.length : 0;
    return (
        <div
            style={{
                display: "flex",
                width: "100%",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Button
                size="small"
                variant="outlined"
                disabled={n === 0}
                onClick={() => props.context?.openOrders?.(props.data)}
                sx={{
                    textTransform: "none",
                    fontSize: 12,
                    py: 0,
                    minWidth: 90,
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.45)",
                    "&:hover": { borderColor: "#ffffff" },
                    "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.3)",
                        borderColor: "rgba(255,255,255,0.15)",
                    },
                }}
            >
                View {n}
            </Button>
        </div>
    );
};

// ============================================================
// Column definition helpers
// ============================================================
const baseCellStyle = { fontSize: 12 };

const col = (
    field,
    headerName,
    minWidth,
    maxWidth,
    cellRenderer = DefaultRenderer,
    extra = {},
) => ({
    field,
    headerName,
    minWidth,
    maxWidth,
    cellRenderer,
    cellStyle: baseCellStyle,
    ...extra,
});

const dateCol = (field, headerName, minWidth = 140, maxWidth = 170) => ({
    colId: field,
    headerName,
    minWidth,
    maxWidth,
    cellRenderer: DateTimeRenderer,
    cellStyle: baseCellStyle,
    valueGetter: (p) => parseDate(p.data?.[field]),
    valueFormatter: (p) => formatDate(p.value),
    filter: "agDateColumnFilter",
    filterParams: { comparator: dateOnlyComparator, browserDatePicker: true },
});

const numCol = (
    field,
    headerName,
    minWidth,
    maxWidth,
    cellRenderer = DefaultRenderer,
) =>
    col(field, headerName, minWidth, maxWidth, cellRenderer, {
        filter: "agNumberColumnFilter",
    });

const boolCol = (field, headerName, minWidth, maxWidth) =>
    col(field, headerName, minWidth, maxWidth, DefaultBooleanRenderer, {
        filterValueGetter: (p) => (p.data?.[field] ? "True" : "False"),
    });

const listCol = (field, headerName, minWidth, maxWidth) =>
    col(field, headerName, minWidth, maxWidth, RolesRenderer, {
        sortable: false,
        // text filter can't search an array, so give it the joined string
        filterValueGetter: (p) => (p.data?.[field] ?? []).join(", "),
        tooltipValueGetter: (p) => (p.data?.[field] ?? []).join(", "),
    });

// Pass this as defaultColDef on <AgGridReact />
export const defaultColDef = {
    sortable: true,
    resizable: true,
    filter: "agTextColumnFilter",
    floatingFilter: true,
};

export const getUserListColumnDef = (auth, locale_string) => {
    let def;

    def = [
        { ...numCol("id", "#", 50, 70), pinned: "left" },
        { ...col("name", "User Name", 180, 200), pinned: "left" },
        col("first_name", "First Name", 100, 140),
        col("last_name", "Last Name", 100, 140),
        col("email", "Email", 260, 280),
        col("mobile", "Mobile", 110, 140),
        boolCol("active", "Active", 70, 90),
        boolCol("is_contact_verified", "Verified", 80, 110),
        col("active_subscriber", "Subscriber", 90, 120, SubscriberRenderer, {
            filterValueGetter: (p) =>
                p.data?.active_subscriber ? "PAID" : "-",
        }),
        numCol("tcm_member_id", "TCM Member ID", 100, 130),
        boolCol("tcm_unsubscribe", "TCM Unsubscribed", 110, 140),
        listCol("roles", "Roles List", 220, 280),
        listCol("resources", "Resources", 220, 320),
        col("last_login_lapse", "Login Since", 120, 160, TimeRenderer, {
            // "HH:MM:SS" strings sort wrongly as text, so sort by the real last_login date
            comparator: (a, b, nodeA, nodeB) => {
                const da = parseDate(nodeA.data?.last_login)?.getTime() ?? 0;
                const db = parseDate(nodeB.data?.last_login)?.getTime() ?? 0;
                return db - da;
            },
        }),
        numCol("n_devices", "# Devices", 60, 100),
        dateCol("last_login", "Last Login On", 120, 160),
        dateCol("account_created", "Account Created On", 120, 160),
        col("active_subscription", "Active Plan", 300, 340),
        dateCol("active_subscription_start", "Plan Start"),
        dateCol("active_subscription_end", "Plan End"),
        numCol(
            "active_subscription_days_left",
            "Days Left",
            80,
            100,
            DaysLeftRenderer,
        ),
        numCol("n_initiated_orders", "Orders Initiated", 90, 110),
        numCol("n_rejected_orders", "Orders Rejected", 90, 110),
        numCol("n_cancelled_orders", "Orders Cancelled", 90, 110),
        numCol("n_consumed_orders", "Orders Consumed", 90, 110),
        col(
            "consumed_orders",
            "Last Consumed Order",
            400,
            450,
            ConsumedOrderRenderer,
            {
                cellStyle: { fontSize: 12, alignSelf: "left", align: "left" },
                filterValueGetter: (p) =>
                    (p.data?.consumed_orders ?? []).join(", "),
                tooltipValueGetter: (p) =>
                    (p.data?.consumed_orders ?? []).join("\n"),
            },
        ),
        col(
            "order_records",
            "Active Order Record (Order Status / Subscription Status)",
            280,
            420,
            SubscriptionListRenderer,
            {
                cellStyle: { fontSize: 12, alignSelf: "left", align: "left" },
                filterValueGetter: (p) =>
                    (p.data?.order_records ?? [])
                        .filter((o) => o.subscription_status == "ACTIVE")
                        .map((o) => o.subscription_name)
                        .join(", "),
                tooltipValueGetter: (p) => {
                    const active = (p.data?.order_records ?? []).filter(
                        (o) => o.subscription_status == "ACTIVE",
                    );
                    return active.length
                        ? active
                              .map(
                                  (o) =>
                                      `${o.subscription_name}: ${o.order_status}/${o.subscription_status} (${o.subscription_age})`,
                              )
                              .join("\n")
                        : "";
                },
            },
        ),
        {
            field: "order_records",
            colId: "order_history",
            headerName: "Order History",
            minWidth: 110,
            maxWidth: 130,
            pinned: "right",
            sortable: false,
            filter: false,
            floatingFilter: false,
            cellRenderer: OrderRecordsRenderer,
        },
    ];

    return def;
};
